/**
 * perforation 类型策略：穿孔（单圆，可在轮廓内任意移动，极限相切）。
 *
 * 几何：
 *   - radius = (radiusMm) × mmToPixelRatio / maskFitScale
 *   - centerPoint = canvas 坐标
 *
 * clampSelf（spec §3.3 outline 约束）：
 *   - 圆心在轮廓内且 dist(closest) < radius → 沿 closest→center 推到 dist = radius（圆与刀线内相切）
 *   - 圆心在轮廓外 → 拉到 closest 后沿轮廓内法线推 radius 距离（圆从内侧与刀线相切）
 *
 * 默认位置（spec §5）：
 *   - 首个实例 → 轮廓几何中心（外接矩形中点）
 *   - 后续实例 → 在已有同 type 实例最右侧的右边偏移 componentMinGapMm + 2*radius
 *
 * 导出（spec §7）：
 *   - perforationPointList:        [[x,y]] 比例 0~1
 *   - originPerforationPointList:  { x, y, baseCanvasWidth, componentId, geometryConfig, ... }
 */

import {
  convertCanvasToBackend,
  findClosestPoint,
  getRestoreBusinessScale,
  isPointInPolygon
} from '../core.js';
import {
  DRAW_TYPE,
  lineColor,
  roundColor,
  DEFAULT_CONFIG
} from '../constants.js';
import { registerComponentType } from '../typeRegistry.js';

/**
 * 校验穿孔 geometryConfig。
 * @param {Object} g
 * @returns {string|null}
 */
function validateGeometryConfig(g) {
  if (!g) return '缺少组件尺寸配置：geometryConfig';
  if (typeof g.radiusMm !== 'number' || g.radiusMm <= 0) {
    return '缺少或非法字段：radiusMm（必须 > 0 的数字）';
  }
  return null;
}

/**
 * 计算半径（px）。
 * @param {Object} geometryConfig
 * @param {Object} sharedState
 * @returns {number}
 */
function computeRadius(geometryConfig, sharedState) {
  const k = sharedState.mmToPixelRatio / (sharedState.maskFitScale || 1);
  return geometryConfig.radiusMm * k;
}

/**
 * 求轮廓外接矩形中心（用作首个实例默认位置）。
 * @param {Array<Array<number>>} points
 * @returns {{x:number,y:number}}
 */
function getOutlineCenter(points) {
  if (!points || !points.length) return { x: 0, y: 0 };
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (p[0] < minX) minX = p[0];
    if (p[0] > maxX) maxX = p[0];
    if (p[1] < minY) minY = p[1];
    if (p[1] > maxY) maxY = p[1];
  }
  return { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
}

/**
 * 以参考实例为基准，向右偏移 minGapPx + 2*radius 作为新位置。
 * @param {Object} referenceInstance - 参考实例（最近选中或最右侧）
 * @param {number} radius - 新实例半径
 * @param {number} minGapPx
 * @returns {{x:number,y:number}}
 */
function getNextRightOf(referenceInstance, radius, minGapPx) {
  return {
    x:
      referenceInstance.centerPoint.x +
      referenceInstance.radius +
      minGapPx +
      radius,
    y: referenceInstance.centerPoint.y
  };
}

/**
 * init：解析尺寸 + 初始位置（placement.centerPoint 优先，否则按 §5.1/§5.2 默认）。
 * @param {Object} instance
 * @param {Object} sharedState
 * @param {Object} options
 * @param {Object} [options.placement] - { centerPoint:{ x, y, baseCanvasWidth? } }
 * @param {Array<Object>} [options.sameTypeInstances]
 * @param {Object} [options.referenceInstance] - 同类型右偏移参考实例
 * @param {number} [options.minGapPx]
 * @param {number} [options.crossTypeOffsetY] - 穿孔↔底座孔交叉偏移（px）
 * @returns {void}
 */
function init(instance, sharedState, options) {
  instance.radius = computeRadius(instance.geometryConfig, sharedState);
  const placement = (options && options.placement) || null;
  let centerPoint = null;
  if (placement && placement.centerPoint) {
    const cp = placement.centerPoint;
    // 按去除留白后的业务坐标宽度缩放，避免 padding 变化导致还原位置偏移。
    if (cp.baseCanvasWidth && cp.baseCanvasWidth !== sharedState.canvasWidth) {
      const k = getRestoreBusinessScale(cp, sharedState);
      centerPoint = { x: cp.x * k, y: cp.y * k };
    } else {
      centerPoint = { x: cp.x, y: cp.y };
    }
  } else {
    const ref = (options && options.referenceInstance) || null;
    const minGapPx = (options && options.minGapPx) || 0;
    if (ref) {
      centerPoint = getNextRightOf(ref, instance.radius, minGapPx);
    }
    if (!centerPoint) {
      centerPoint = getOutlineCenter(sharedState.outlinePoints);
      // 交叉偏移：穿孔↔底座孔首次添加时向下偏移避免重叠
      const crossOffsetY = (options && options.crossTypeOffsetY) || 0;
      if (crossOffsetY) {
        centerPoint = { x: centerPoint.x, y: centerPoint.y + crossOffsetY };
      }
    }
  }
  instance.centerPoint = centerPoint;
}

/**
 * 把 centerPoint 收敛到合法位置；本 type 永远尝试收敛到相切，不算违规。
 * @param {Object} instance
 * @param {Object} sharedState
 * @returns {boolean}
 */
function clampSelf(instance, sharedState) {
  const points = sharedState.outlinePoints;
  if (!points || !points.length) return false;
  const closest = findClosestPoint(instance.centerPoint, points);
  if (!closest) return false;
  const dx = instance.centerPoint.x - closest.x;
  const dy = instance.centerPoint.y - closest.y;
  const dist = Math.sqrt(dx * dx + dy * dy) || 1;
  const inside = isPointInPolygon(instance.centerPoint, points);
  const r = instance.radius;
  // 组件边缘与刀线的最小间距（px），0 = 相切
  const outlineGap = sharedState.componentToOutlineGapPx || 0;
  const targetDist = r + outlineGap;
  if (inside) {
    if (dist < targetDist) {
      const ratio = targetDist / dist;
      instance.centerPoint.x = closest.x + dx * ratio;
      instance.centerPoint.y = closest.y + dy * ratio;
    }
  } else {
    const ratio = targetDist / dist;
    instance.centerPoint.x = closest.x - dx * ratio;
    instance.centerPoint.y = closest.y - dy * ratio;
  }
  return false;
}

function hitTest(instance, point) {
  const dx = point.x - instance.centerPoint.x;
  const dy = point.y - instance.centerPoint.y;
  return dx * dx + dy * dy <= instance.radius * instance.radius;
}

/**
 * 获取编辑态命中信息；小尺寸穿孔补足到最小热区。
 * @param {Object} instance
 * @param {{x:number,y:number}} point
 * @returns {{hit:boolean,isRealHit:boolean,distance:number}}
 */
function getHitTestInfo(instance, point) {
  const dx = point.x - instance.centerPoint.x;
  const dy = point.y - instance.centerPoint.y;
  const distance = Math.sqrt(dx * dx + dy * dy);
  const realRadius = instance.radius;
  const hitRadius = Math.max(
    realRadius + DEFAULT_CONFIG.componentLargeHitPaddingPx,
    DEFAULT_CONFIG.componentMinHitSizePx / 2
  );
  return {
    hit: distance <= hitRadius,
    isRealHit: distance <= realRadius,
    distance
  };
}

function onDragStart(instance, point) {
  instance._dragOffset = {
    dx: point.x - instance.centerPoint.x,
    dy: point.y - instance.centerPoint.y
  };
}

function onDragMove(instance, point, sharedState) {
  const off = instance._dragOffset || { dx: 0, dy: 0 };
  instance.centerPoint.x = point.x - off.dx;
  instance.centerPoint.y = point.y - off.dy;
  clampSelf(instance, sharedState);
}

function onDragEnd(instance) {
  instance._dragOffset = null;
}

/**
 * 编辑模式：蓝色单圆。
 */
function drawEdit(instance, ctx, sharedState) {
  ctx.save();
  ctx.strokeStyle = roundColor;
  ctx.lineWidth = sharedState.lineWidth;
  ctx.setLineDash([]);
  ctx.beginPath();
  ctx.arc(
    instance.centerPoint.x,
    instance.centerPoint.y,
    instance.radius,
    0,
    Math.PI * 2
  );
  ctx.stroke();
  ctx.restore();
}

/**
 * 预览模式：刀线圆弧桥接 + 完整圆描边。
 * 刀线点已在 outlineRenderer.drawOutline 中过滤掉圆内部的点，这里画圆弧补缺口。
 */
function drawPreview(instance, ctx, sharedState) {
  var points = sharedState.outlinePoints;
  var currentLineColor = sharedState.lineColor || lineColor;
  if (!points || !points.length) {
    // 无刀线时仍画完整圆
    ctx.save();
    ctx.strokeStyle = currentLineColor;
    ctx.lineWidth = sharedState.lineWidth;
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.arc(
      instance.centerPoint.x,
      instance.centerPoint.y,
      instance.radius,
      0,
      Math.PI * 2
    );
    ctx.stroke();
    ctx.restore();
    return;
  }

  // 找刀线进入/退出圆的点（与 keyHole 同算法）
  var inPoints = [];
  var outPoints = [];
  for (var i = 0; i < points.length; i++) {
    var p = points[i];
    var prevIdx = i === 0 ? points.length - 1 : i - 1;
    var prev = points[prevIdx];
    var dCurI = Math.hypot(
      p[0] - instance.centerPoint.x,
      p[1] - instance.centerPoint.y
    );
    var dPrevI = Math.hypot(
      prev[0] - instance.centerPoint.x,
      prev[1] - instance.centerPoint.y
    );
    var curIn = dCurI <= instance.radius;
    var prevIn = dPrevI <= instance.radius;
    if (!prevIn && curIn) inPoints.push({ index: i, point: p });
    else if (prevIn && !curIn) outPoints.push({ index: i, point: p });
  }

  ctx.save();
  ctx.strokeStyle = currentLineColor;
  ctx.lineWidth = sharedState.lineWidth;
  ctx.setLineDash([]);

  // 画圆弧桥接刀线缺口（退出点 → 进入点，沿圆边缘）
  if (inPoints.length > 0 && outPoints.length > 0) {
    var inPt = inPoints[0];
    var outPt = outPoints[outPoints.length - 1];
    var startAngle = Math.atan2(
      outPt.point[1] - instance.centerPoint.y,
      outPt.point[0] - instance.centerPoint.x
    );
    var endAngle = Math.atan2(
      inPt.point[1] - instance.centerPoint.y,
      inPt.point[0] - instance.centerPoint.x
    );
    ctx.beginPath();
    ctx.arc(
      instance.centerPoint.x,
      instance.centerPoint.y,
      instance.radius,
      startAngle,
      endAngle
    );
    ctx.stroke();
  }

  // 完整圆红色描边
  ctx.beginPath();
  ctx.arc(
    instance.centerPoint.x,
    instance.centerPoint.y,
    instance.radius,
    0,
    Math.PI * 2
  );
  ctx.stroke();
  ctx.restore();
}

function getCollisionShape(instance) {
  return {
    kind: 'circle',
    cx: instance.centerPoint.x,
    cy: instance.centerPoint.y,
    r: instance.radius
  };
}

function getSelectionRect(instance) {
  const r = instance.radius + 1;
  return {
    x: instance.centerPoint.x - r,
    y: instance.centerPoint.y - r,
    w: r * 2,
    h: r * 2
  };
}

function getMagnifierAnchor(instance) {
  return { x: instance.centerPoint.x, y: instance.centerPoint.y };
}

/**
 * 导出 layerData 片段。
 */
function toExport(instance, sharedState) {
  const backendPoint = convertCanvasToBackend(
    instance.centerPoint,
    sharedState
  );
  const perforationPoint = [
    +(backendPoint.x / sharedState.imageInfo.width).toFixed(6),
    +(backendPoint.y / sharedState.imageInfo.height).toFixed(6)
  ];
  const originItem = {
    x: instance.centerPoint.x,
    y: instance.centerPoint.y,
    baseCanvasWidth: sharedState.canvasWidth,
    canvasRoundWhiteSpace: sharedState.canvasRoundWhiteSpace,
    componentId: instance.id,
    geometryConfig: instance.geometryConfig,
    sizePresetId: instance.sizePresetId || '',
    enableMagnifier: instance.enableMagnifier !== false
  };
  return {
    perforationPointList: [perforationPoint],
    originPerforationPointList: [originItem]
  };
}

function serializeForDirty(instance) {
  return {
    cx: Math.round(instance.centerPoint.x * 100) / 100,
    cy: Math.round(instance.centerPoint.y * 100) / 100
  };
}

const perforationStrategy = {
  type: DRAW_TYPE.PERFORATION,
  validateGeometryConfig,
  init,
  clampSelf,
  hitTest,
  getHitTestInfo,
  onDragStart,
  onDragMove,
  onDragEnd,
  drawEdit,
  drawPreview,
  getCollisionShape,
  getSelectionRect,
  getMagnifierAnchor,
  toExport,
  serializeForDirty
};

registerComponentType(perforationStrategy);

export default perforationStrategy;
